import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import { useList } from '../lib/useList.ts';

interface Entry {
  audit_id: number;
  created_at: string;
  entity_type: string;
  entity_id: number | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  user_name: string;
  kind: string;
  label: string | null;
  change: string;
}
interface Kind { entity_type: string; kind: string; entries: string }

const ACTION_TAG: Record<string, string> = {
  INSERT: 'ready', UPDATE: 'soon', DELETE: 'blocked',
};
const ACTION_LABEL: Record<string, string> = {
  INSERT: 'Created', UPDATE: 'Changed', DELETE: 'Deleted',
};

function fmtWhen(s: string): string {
  return new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

/**
 * Everything that has changed.
 *
 * The naming and the "what changed" sentence are computed in the view rather
 * than here, because the grid has to be able to sort and filter on them -- you
 * cannot filter on a column built after the query has run.
 */
export default function Logs() {
  const list = useList<Entry>('/api/logs', 'logs', { sort: 'created_at', dir: 'desc' });
  const [kinds, setKinds] = useState<Kind[]>([]);

  useEffect(() => {
    api.get<{ kinds: Kind[] }>('/api/logs/kinds')
      .then((r) => setKinds(r.kinds))
      .catch(() => setKinds([]));
  }, []);

  const columns: Column<Entry>[] = [
    { key: 'created_at', label: 'When', type: 'date',
      render: (r) => (
        <span className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.created_at)}</span>
      ) },
    { key: 'user_name', label: 'Who' },
    { key: 'action', label: 'Action', type: 'choice',
      choices: Object.entries(ACTION_LABEL).map(([value, label]) => ({ value, label })),
      render: (r) => (
        <span className={`tag ${ACTION_TAG[r.action]}`}>{ACTION_LABEL[r.action]}</span>
      ) },
    { key: 'kind', label: 'Record type', type: 'choice',
      choices: kinds.map((k) => ({ value: k.kind, label: `${k.kind} (${k.entries})` })),
      render: (r) => r.kind },
    { key: 'label', label: 'Record',
      render: (r) => <span className="mono dim">{r.label ?? '—'}</span> },
    { key: 'change', label: 'Change',
      render: (r) => <span className="dim">{r.change}</span> },
  ];

  return (
    <div className="panel">
      <DataTable
        list={list}
        columns={columns}
        exportName="/api/logs"
        rowKey={(r) => r.audit_id}
        empty="Nothing recorded yet."
      />
    </div>
  );
}
